import { getFileBlob } from "./offline-store.js";

const ENTITY_KEYS = [
  "milestones",
  "tasks",
  "expenses",
  "materials",
  "custom",
  "appliances",
  "furniture",
  "journals",
  "orders",
  "payments",
  "documents",
  "auditLog",
  "packages",
  "specItems",
  "roomMeasures",
  "workItems",
  "sundries",
];

function clone(value) {
  return JSON.parse(JSON.stringify(value));
}

function dataUrlToBlob(dataUrl) {
  const [header, encoded] = dataUrl.split(",");
  const mime = header.match(/^data:([^;]+);base64$/)?.[1] || "image/jpeg";
  const binary = atob(encoded);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return new Blob([bytes], { type: mime });
}

async function digestBlob(blob) {
  const digest = await crypto.subtle.digest("SHA-256", await blob.arrayBuffer());
  return Array.from(new Uint8Array(digest))
    .slice(0, 12)
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

function fileExtension(mime, fileName = "") {
  const name = String(fileName || "");
  const fromName = name.includes(".") ? name.split(".").pop().toLowerCase() : "";
  if (fromName && /^[a-z0-9]{2,5}$/.test(fromName)) return fromName;
  if (mime === "application/pdf") return "pdf";
  return imageExtension(mime);
}

function sanitizeDocument(record) {
  const next = { ...record };
  next.files = (next.files || []).map((file) => {
    const copy = { ...file };
    delete copy.fileUrl;
    delete copy.dataUrl;
    return copy;
  });
  return next;
}

async function uploadDocumentAttachments(client, userId, projectId, database) {
  const next = clone(database);
  next.documents = await Promise.all(
    (next.documents || []).map(async (record) => {
      const files = [];
      for (const file of record.files || []) {
        const copy = { ...file };
        delete copy.fileUrl;
        delete copy.dataUrl;
        if (!copy.filePath && copy.localKey) {
          const blob = await getFileBlob(copy.localKey);
          if (blob) {
            const digest = await digestBlob(blob);
            const path = `${userId}/${projectId}/documents/${record.id}-${copy.id}-${digest}.${fileExtension(copy.mime || blob.type, copy.fileName)}`;
            const { error } = await client.storage
              .from("casa-media")
              .upload(path, blob, { contentType: copy.mime || blob.type || "application/octet-stream", upsert: true });
            if (error) throw error;
            copy.filePath = path;
          }
        }
        files.push(copy);
      }
      return { ...record, files };
    }),
  );
  return next;
}

async function attachSignedDocumentFiles(client, database) {
  const next = clone(database);
  const files = [];
  for (const record of next.documents || []) {
    for (const file of record.files || []) {
      if (file.filePath) files.push(file);
    }
  }
  await Promise.all(
    files.map(async (file) => {
      const { data, error } = await client.storage
        .from("casa-media")
        .createSignedUrl(file.filePath, 60 * 60 * 24);
      if (!error) file.fileUrl = data.signedUrl;
    }),
  );
  return next;
}

function imageExtension(mime) {
  if (mime === "image/png") return "png";
  if (mime === "image/webp") return "webp";
  if (mime === "image/gif") return "gif";
  return "jpg";
}

async function uploadRecordImage(client, userId, projectId, entity, record) {
  if (!String(record.image || "").startsWith("data:image/")) return record;
  const next = { ...record };
  const blob = dataUrlToBlob(next.image);
  const digest = await digestBlob(blob);
  const path = `${userId}/${projectId}/${entity}/${record.id}-${digest}.${imageExtension(blob.type)}`;
  const { error } = await client.storage
    .from("casa-media")
    .upload(path, blob, { contentType: blob.type, upsert: true });
  if (error) throw error;
  next.image = "";
  next.imagePath = path;
  return next;
}

async function uploadRecordPhotos(client, userId, projectId, entity, record) {
  const next = { ...record };
  next.photos = await Promise.all(
    (next.photos || []).map(async (photo, index) => {
      if (!String(photo.image || "").startsWith("data:image/")) return photo;
      const blob = dataUrlToBlob(photo.image);
      const digest = await digestBlob(blob);
      const path = `${userId}/${projectId}/${entity}/${record.id}-${photo.id || index}-${digest}.${imageExtension(blob.type)}`;
      const { error } = await client.storage
        .from("casa-media")
        .upload(path, blob, { contentType: blob.type, upsert: true });
      if (error) throw error;
      return { ...photo, image: "", imagePath: path };
    }),
  );
  return next;
}

export async function uploadEmbeddedImages(client, userId, projectId, database) {
  const next = clone(database);
  for (const entity of ["materials", "custom", "appliances", "furniture", "journals", "specItems", "sundries"]) {
    next[entity] = await Promise.all(
      (next[entity] || []).map((record) =>
        uploadRecordImage(client, userId, projectId, entity, record),
      ),
    );
  }
  next.workItems = await Promise.all(
    (next.workItems || []).map((record) =>
      uploadRecordPhotos(client, userId, projectId, "workItems", record),
    ),
  );
  return next;
}

async function attachSignedImages(client, database) {
  const next = clone(database);
  const rows = [];
  for (const entity of ["materials", "custom", "appliances", "furniture", "journals", "specItems", "sundries"]) {
    for (const record of next[entity] || []) {
      if (record.imagePath) rows.push(record);
    }
  }
  for (const record of next.workItems || []) {
    for (const photo of record.photos || []) {
      if (photo.imagePath) rows.push(photo);
    }
  }
  await Promise.all(
    rows.map(async (record) => {
      const { data, error } = await client.storage
        .from("casa-media")
        .createSignedUrl(record.imagePath, 60 * 60 * 24);
      if (!error) record.image = data.signedUrl;
    }),
  );
  return next;
}

export function flattenDatabase(database) {
  const rows = [];
  for (const entityType of ENTITY_KEYS) {
    for (const record of database[entityType] || []) {
      rows.push({
        entity_type: entityType,
        record_id: String(record.id),
        payload: entityType === "documents" ? sanitizeDocument(record) : record,
      });
    }
  }
  rows.push({
    entity_type: "_meta",
    record_id: "singleton",
    payload: database.meta || {},
  });
  return rows;
}

export function inflateDatabase(rows) {
  const database = Object.fromEntries(ENTITY_KEYS.map((key) => [key, []]));
  database.meta = {};
  for (const row of rows || []) {
    if (row.entity_type === "_meta") {
      database.meta = row.payload || {};
    } else if (ENTITY_KEYS.includes(row.entity_type)) {
      database[row.entity_type].push(row.payload);
    }
  }
  return database;
}

export async function ensureProject(client, user, name = "西派樘樾") {
  const { data, error } = await client.rpc("casa_ensure_project", {
    p_name: name,
  });
  if (!error && data) {
    return Array.isArray(data) ? data[0] : data;
  }

  const { data: existing, error: selectError } = await client
    .from("casa_projects")
    .select("id,name,revision,updated_at,updated_by,device_id")
    .eq("owner_id", user.id)
    .maybeSingle();
  if (selectError) throw selectError;
  if (existing) return existing;
  if (error) throw error;
  throw new Error("无法创建云端项目，请重新执行最新建表 SQL");
}

export async function pushDatabase(
  client,
  { user, project, database, expectedRevision, updatedBy, deviceId },
) {
  const uploadReady = await uploadDocumentAttachments(
    client,
    user.id,
    project.id,
    await uploadEmbeddedImages(client, user.id, project.id, database),
  );
  const { data, error } = await client.rpc("casa_sync_records", {
    p_project_id: project.id,
    p_expected_revision: Number(expectedRevision || 0),
    p_records: flattenDatabase(uploadReady),
    p_updated_by: updatedBy,
    p_device_id: deviceId,
  });
  if (error) throw error;
  return { result: data, database: uploadReady };
}

export async function refreshSignedMedia(client, database) {
  return attachSignedDocumentFiles(
    client,
    await attachSignedImages(client, clone(database)),
  );
}

export async function pullDatabase(client, project) {
  const { data: currentProject, error: projectError } = await client
    .from("casa_projects")
    .select("id,name,revision,updated_at,updated_by,device_id")
    .eq("id", project.id)
    .single();
  if (projectError) throw projectError;

  const { data: rows, error: recordsError } = await client
    .from("casa_records")
    .select("entity_type,record_id,payload,updated_at,updated_by,device_id")
    .eq("project_id", project.id)
    .eq("deleted", false);
  if (recordsError) throw recordsError;

  return {
    project: currentProject,
    database: await attachSignedDocumentFiles(
      client,
      await attachSignedImages(client, inflateDatabase(rows)),
    ),
  };
}

export function subscribeToProject(client, projectId, onRevision) {
  return client
    .channel(`casa-project-${projectId}`)
    .on(
      "postgres_changes",
      {
        event: "UPDATE",
        schema: "public",
        table: "casa_projects",
        filter: `id=eq.${projectId}`,
      },
      (message) => onRevision(message.new),
    )
    .subscribe();
}
